<template>
  <div class="lucky-numbers-view">
    <SectionHeader 
      :title="$t('sections.luckyNumberGames')"
      :subtitle="isLite ? '' : $t('sections.luckyNumberGamesDescription')"
      variant="complex"
    >
      <template #right>
        <div class="header-filters">
          <LotteryFilters 
            :active-country-slugs="activeCountrySlugs"
            @search="handleSearch"
            @filterCountry="handleCountryFilter"
          />
        </div>
      </template>
    </SectionHeader>

    <BetBuilderPanel v-if="!isLite" />

    <LuckyNumbersSection
      :games="upcomingDrawsStore.draws"
      :is-favorited="isFavorited"
      :show-filters="false"
      :search-query="searchQuery"
      :selected-country="selectedCountry"
      @bet="handleBet"
      @favorite="handleFavorite"
      @remove="removeLottery"
    >
      <template #header>
        <!-- Header is handled above -->
        <div style="display: none;" />
      </template>
    </LuckyNumbersSection>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import SectionHeader from '@/components/base/SectionHeader.vue'
import LotteryFilters from '@/components/LotteryFilters.vue'
import LuckyNumbersSection from '@/components/LuckyNumbersSection.vue'
import BetBuilderPanel from '@/components/BetBuilderPanel.vue'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import type { UpcomingDraw } from '@/types/lottery'

const router = useRouter()
const route = useRoute()
const upcomingDrawsStore = useUpcomingDrawsStore()
const { isLite } = useIntegrationMode()
const favorites = ref<UpcomingDraw[]>([])
const searchQuery = ref('')
const selectedCountry = ref('')

const activeCountrySlugs = computed(() => {
  const slugs = new Set<string>()
  for (const draw of upcomingDrawsStore.draws || []) {
    if (draw?.country) slugs.add(String(draw.country).toLowerCase())
  }
  return Array.from(slugs)
})

const handleSearch = (query: string) => {
  searchQuery.value = query
}

const handleCountryFilter = (country: string) => {
  selectedCountry.value = country
}

const handleBet = (draw: UpcomingDraw) => {
  router.push({ path: `/lottery/${draw.game_slug}`, query: route.query })
}

const handleFavorite = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return
  const index = favorites.value.findIndex(fav => fav.id === draw.id)
  if (index === -1) {
    favorites.value.push(draw)
  } else {
    favorites.value.splice(index, 1)
  }
  localStorage.setItem('favoriteDraws', JSON.stringify(favorites.value))
}

const removeLottery = (lotteryToRemove: UpcomingDraw) => {
  const idx = upcomingDrawsStore.draws.findIndex((draw) => draw.id === lotteryToRemove.id)
  if (idx !== -1) {
    upcomingDrawsStore.draws.splice(idx, 1)
    if (upcomingDrawsStore.draws.length === 0) {
      void upcomingDrawsStore.fetchDraws(1)
    }
  }
}

const isFavorited = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return false
  return favorites.value.some(fav => fav.id === draw.id)
}

onMounted(async () => {
  const savedFavorites = localStorage.getItem('favoriteDraws')
  if (savedFavorites) {
    favorites.value = JSON.parse(savedFavorites)
  }
  await upcomingDrawsStore.fetchDraws(1)
})
</script>

<style scoped>
.lucky-numbers-view {
  padding: 5px;
}
.header-filters {
  display: flex;
  align-items: center;
  width: 100%;
}

:deep(.section-header.complex) {
  padding: 0;
  /* margin-bottom: 32px; */
  border-bottom: none;
  align-items: flex-start;
}

:deep(.filters-bar) {
  margin-bottom: 0;
  justify-content: flex-end;
  gap: 20px;
}

:deep(.filter-item), :deep(.search-group) {
  max-width: 320px;
}

@media (max-width: 1024px) {
  :deep(.filters-bar) {
    flex-wrap: nowrap;
  }
}

@media (max-width: 768px) {
  .lucky-numbers-view {
    padding: 20px 10px;
  }
  
  :deep(.section-header.complex) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  :deep(.filters-bar) {
    justify-content: flex-start;
  }
}
</style>
